import { createContext, useContext, useEffect, useState, useCallback, PropsWithChildren } from "react";
import { useApiClient } from "../api/useApiClient";
import type { ContadoresBandejas } from "../types/reviewItem";

/**
 * Contadores por bandeja, para los badges del menú lateral.
 *
 * Vive en un contexto (y no dentro de cada página) porque el menú se ve
 * SIEMPRE, desde cualquier página. Expone `refrescar()` para que después de
 * autorizar/rechazar/reabrir el badge no quede viejo: sin eso, autorizar una
 * factura la saca de la lista pero el menú seguiría diciendo "63".
 */
interface ValorContexto {
  contadores: ContadoresBandejas | null;
  refrescar: () => void;
}

const ContadoresContext = createContext<ValorContexto>({
  contadores: null,
  refrescar: () => {},
});

export function ContadoresProvider({ children }: PropsWithChildren) {
  const api = useApiClient();
  const [contadores, setContadores] = useState<ContadoresBandejas | null>(null);

  const refrescar = useCallback(() => {
    api
      .obtenerContadores()
      .then(setContadores)
      .catch((err) => {
        // Deliberadamente silencioso: los badges son informativos. Si esto
        // falla (backend frío, rol sin permiso), la navegación tiene que
        // seguir funcionando igual — simplemente no se muestran los números.
        // eslint-disable-next-line no-console
        console.warn("No se pudieron obtener los contadores de bandejas:", err);
        setContadores(null);
      });
  }, [api]);

  useEffect(() => {
    refrescar();
  }, [refrescar]);

  return (
    <ContadoresContext.Provider value={{ contadores, refrescar }}>
      {children}
    </ContadoresContext.Provider>
  );
}

export function useContadores() {
  return useContext(ContadoresContext);
}
